import { useEffect, useState } from 'react'
import { ChevronRight, Loader2, Pencil, Plus, Shapes, Shuffle } from 'lucide-react'
import { Drawer, DrawerField, DrawerSection } from '@/shared/ui/Drawer'
import { ErpTable, StatusBadge, Td, Th } from '@/components/erp/primitives'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { useCan } from '@/shared/auth/session'
import { fmtDateTime, fmtNumber } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import { getUnit } from './api'
import { BoundaryPanel } from './BoundaryPanel'
import { UnitPath } from './UnitPath'
import { AgentCell, MoodCell, RiskBadge, TurnoutCell, isUnassessed } from './IntelCells'
import { AssessmentDialog, StatisticsDialog } from './UnitIntelDialogs'
import {
  UNIT_TYPE_LABEL, childTypeOf, geometryKindOf,
  type UnitDetailResponse, type UnitResponse,
} from '@/shared/api/types'

function childLabel(unitType: string): string {
  const child = childTypeOf(unitType)
  return child ? (UNIT_TYPE_LABEL[child] ?? child).toLowerCase() : 'child'
}

const CENTROID_SOURCE_LABEL: Record<string, string> = {
  DERIVED: 'Derived from the boundary',
  MANUAL: 'Entered by hand',
  IMPORTED: 'From the import file',
  UNKNOWN: 'Unknown',
}

function yesNo(v: boolean | null | undefined) {
  if (v === null || v === undefined) return '—'
  return v ? 'Yes' : 'No'
}

/**
 * Unit detail canvas: identity, hierarchy, register figures, geometry, the
 * intelligence overlay, election statistics and — for a station — its facility.
 *
 * Two sections carry their own action in the header rather than in the footer,
 * because both change one section's data and nothing else: **Record/Edit** on
 * statistics (`geography:update`) and **Assess** on intelligence
 * (`geography:overlay`, a separate grant — publishing a risk assessment and
 * renaming a ward are different powers).
 *
 * The Intelligence section renders even when everything in it is null. An
 * unassessed ward is a fact worth seeing, and hiding the section would make
 * "nobody has looked" indistinguishable from "there is no such thing".
 */
export function UnitDetailDrawer({
  unitId, onClose, onEdit, onChangeStatus, onNavigate, onChanged, onAddChild,
}: {
  unitId: string | null
  onClose: () => void
  onEdit: (d: UnitDetailResponse) => void
  onChangeStatus: (d: UnitDetailResponse) => void
  onNavigate: (id: string) => void
  onChanged: () => void
  /** Create a unit beneath this one — the natural place to start, from its parent. */
  onAddChild?: (u: UnitResponse) => void
}) {
  const canUpdate = useCan('geography:update')
  const canAssess = useCan('geography:overlay')
  const [statsOpen, setStatsOpen] = useState(false)
  const [assessOpen, setAssessOpen] = useState(false)

  /** Re-read the unit after an edit made from inside the drawer. */
  const reload = () => {
    onChanged()
    if (unitId) void getUnit(unitId).then(setData)
  }
  const [data, setData] = useState<UnitDetailResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<'detail' | 'boundary'>('detail')

  useEffect(() => {
    setData(null)
    setError(null)
    setTab('detail')
    if (!unitId) return
    let cancelled = false
    setLoading(true)
    getUnit(unitId)
      .then((d) => { if (!cancelled) setData(d) })
      .catch((err) => { if (!cancelled) setError(explain(err)) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [unitId])

  const u = data?.unit
  /**
   * Ancestors, ROOT FIRST. The API returns them nearest-first — ward, constituency,
   * county, country — which is the opposite of how anybody reads a place, so every
   * consumer here reverses once rather than each of them getting it wrong separately.
   * The immediate parent is therefore `ancestors[0]` in the API's order.
   */
  const lineage = [...(data?.ancestors ?? [])].reverse()
  const parent = data?.ancestors[0]
  const isStation = u?.unitType === 'POLLING_STATION'
  const kind = u?.geometryKind ?? geometryKindOf(u?.unitType)
  /** Served now; the `areaSqKm` proxy remains only for a response from an older build. */
  const hasBoundary = u?.hasBoundary ?? (u?.areaSqKm != null)

  return (
    <Drawer
      open={Boolean(unitId)}
      onClose={onClose}
      title={u?.name ?? 'Unit'}
      subtitle={u ? `${UNIT_TYPE_LABEL[u.unitType] ?? u.unitType} · ${u.code}` : undefined}
      right={u ? <StatusBadge status={u.status} /> : undefined}
      width="lg"
      footer={canUpdate && data ? (
        <>
          {onAddChild && !isStation && (
            <button className="erp-btn mr-auto" onClick={() => onAddChild(data.unit)}>
              <Plus className="h-3.5 w-3.5" />
              Add {childLabel(data.unit.unitType)}
            </button>
          )}
          <button className="erp-btn" onClick={() => onChangeStatus(data)}>
            <Shuffle className="h-3.5 w-3.5" /> Change status
          </button>
          <button className="erp-btn erp-btn-primary" onClick={() => onEdit(data)}>
            <Pencil className="h-3.5 w-3.5" /> Edit
          </button>
        </>
      ) : undefined}
    >
      <ErrorStrip message={error} />
      {loading && !data && (
        <div className="px-4 py-10 text-center text-erp-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
        </div>
      )}

      {data && u && (
        <>
          {u.status === 'MERGED' && u.mergedIntoId && (
            <div className="p-4 pb-0">
              <Note tone="info">
                This unit was merged.{' '}
                <button className="erp-link" onClick={() => onNavigate(u.mergedIntoId!)}>
                  Open the unit it merged into
                </button>
                . It is kept because historical records still reference it.
              </Note>
            </div>
          )}

          {/* Breadcrumb — root first, clickable, the unit itself last and inert. */}
          {lineage.length > 0 && (
            <nav className="px-4 py-2 border-b border-border flex items-center gap-1 flex-wrap text-erp-xs">
              {lineage.map((a) => (
                <span key={a.id} className="inline-flex items-center gap-1">
                  <button className="erp-link" onClick={() => onNavigate(a.id)}>{a.name}</button>
                  <ChevronRight className="h-3 w-3 text-muted-foreground" />
                </span>
              ))}
              <span className="text-muted-foreground">{u.name}</span>
            </nav>
          )}

          <div className="border-b border-border">
            <div className="flex items-center gap-1 px-2">
              {([
                { id: 'detail', label: 'Detail' },
                // One tab for both geometries: a station's pin and a ward's polygon
                // are the same question — "where is this?" — answered differently.
                { id: 'boundary', label: isStation ? 'Location' : 'Boundary' },
              ] as const).map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={cn(
                    'h-9 px-3 text-erp-sm border-b-2 -mb-px',
                    tab === t.id
                      ? 'border-chocolate text-chocolate font-semibold'
                      : 'border-transparent text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {tab === 'boundary' ? (
            <BoundaryPanel unit={u} version={u.version} onChanged={reload} />
          ) : (
            <>
              <DrawerSection title="Identity">
                <DrawerField label="Name" value={u.name} />
                <DrawerField label="Type" value={UNIT_TYPE_LABEL[u.unitType] ?? u.unitType} />
                <DrawerField label="IEBC code" value={u.code} mono />
                <DrawerField label="Region" value={u.region} />
                <DrawerField label="Status" value={<StatusBadge status={u.status} />} />
              </DrawerSection>

              <DrawerSection title="Hierarchy">
                <DrawerField
                  label="Parent"
                  value={u.parentId
                    ? <button className="erp-link" onClick={() => onNavigate(u.parentId!)}>
                        {/* Never the raw id. `parentName` is not populated on this route, so
                            the nearest ancestor is the real source — and its code is a far
                            better last resort than a uuid nobody can act on. */}
                        {u.parentName ?? parent?.name ?? parent?.code ?? 'Open parent'}
                      </button>
                    : <span className="text-muted-foreground">none — this is the root</span>}
                />
                <DrawerField
                  label="Path"
                  value={
                    /* The ancestors arrive with this unit, so the readable chain costs no
                       extra request. The ltree codes stay on hover for anyone reconciling
                       against an IEBC spreadsheet. */
                    <UnitPath
                      levels={[
                        ...lineage.map((a) => ({ code: a.code, name: a.name })),
                        { code: u.code, name: u.name },
                      ]}
                      path={u.path}
                      className="justify-start"
                    />
                  }
                />
                <DrawerField label="Depth" value={<span className="erp-metric">{u.depth}</span>} />
                <DrawerField
                  label="Direct children"
                  value={<span className="erp-metric">{fmtNumber(data.childCounts.children)}</span>}
                />
              </DrawerSection>

              <DrawerSection title="Register">
                <DrawerField
                  label="Registered voters"
                  value={<span className="erp-metric">{fmtNumber(u.registeredVoters)}</span>}
                />
                <DrawerField
                  label="Polling stations"
                  value={
                    <span className="erp-metric" title="Maintained rollup, reconciled nightly">
                      {fmtNumber(u.stationCount)}
                    </span>
                  }
                />
                <DrawerField label="Population" value={u.population === null || u.population === undefined ? '—' : fmtNumber(u.population)} />
                <DrawerField label="Area" value={u.areaSqKm ? `${fmtNumber(Math.round(u.areaSqKm))} km²` : '—'} />
              </DrawerSection>

              <DrawerSection title={isStation ? 'Location' : 'Centroid'}>
                <DrawerField
                  label="Geometry"
                  value={
                    <span className="inline-flex items-center gap-1.5">
                      <Shapes className="h-3 w-3 text-muted-foreground" />
                      {kind === 'POINT' ? 'Point-based' : 'Boundary-based'}
                      {kind === 'BOUNDARY' && (
                        <span className="text-muted-foreground">
                          · {hasBoundary ? 'boundary set' : 'no boundary yet'}
                        </span>
                      )}
                    </span>
                  }
                />
                <DrawerField
                  label="Latitude"
                  value={u.latitude === null || u.latitude === undefined ? '—' : u.latitude.toFixed(6)}
                  mono
                />
                <DrawerField
                  label="Longitude"
                  value={u.longitude === null || u.longitude === undefined ? '—' : u.longitude.toFixed(6)}
                  mono
                />
                {kind === 'BOUNDARY' && u.latitude != null && (
                  <DrawerField
                    label="Pin source"
                    value={u.centroidSource
                      ? CENTROID_SOURCE_LABEL[u.centroidSource] ?? u.centroidSource
                      : hasBoundary
                        ? 'Derived from the boundary'
                        : 'Entered by hand — a placeholder until a boundary is uploaded'}
                  />
                )}
              </DrawerSection>

              {/*
                The four intelligence values. Rendered even when empty — an
                unassessed ward is a fact worth seeing, and hiding the section
                would make "nobody has looked" indistinguishable from "there is
                no such thing". Backend prompt 10 fills these in.
              */}
              <DrawerSection
                title="Intelligence"
                right={canAssess && u.status === 'ACTIVE' ? (
                  <button className="erp-link text-erp-xs" onClick={() => setAssessOpen(true)}>
                    Assess
                  </button>
                ) : undefined}
              >
                <DrawerField label="Turnout" value={<TurnoutCell overlay={u.overlay} />} />
                <DrawerField label="Mood" value={<MoodCell overlay={u.overlay} />} />
                <DrawerField label="Risk" value={<RiskBadge level={u.overlay?.riskLevel} />} />
                <DrawerField
                  label={isStation ? 'Agent' : 'Agent coverage'}
                  value={<AgentCell unit={u} />}
                />
                {isUnassessed(u.overlay) && (
                  <div className="px-4 pb-3 -mt-1">
                    <p className="text-erp-xs text-muted-foreground">
                      Nothing has been assessed for this unit yet. These fill in as
                      register statistics are recorded and field intelligence arrives —
                      they are never defaulted, because “not assessed” and “assessed as
                      low risk” are different answers.
                    </p>
                  </div>
                )}
              </DrawerSection>

              {isStation && data.station && (
                <DrawerSection title="Facility">
                  <DrawerField
                    label="Location type"
                    value={data.station.locationType
                      ? data.station.locationType.charAt(0)
                        + data.station.locationType.slice(1).replace(/_/g, ' ').toLowerCase()
                      : '—'}
                  />
                  <DrawerField
                    label="Streams"
                    value={data.station.streamCount == null
                      ? '—'
                      : <span className="erp-metric">{data.station.streamCount}</span>}
                  />
                  <DrawerField label="Power" value={yesNo(data.station.hasPower)} />
                  <DrawerField label="Network" value={yesNo(data.station.hasNetwork)} />
                  <DrawerField label="Step-free access" value={yesNo(data.station.disabledAccess)} />
                  <DrawerField
                    label="Elevation"
                    value={data.station.elevationM == null ? '—' : `${data.station.elevationM} m`}
                  />
                </DrawerSection>
              )}

              {(data.statistics.length > 0 || canUpdate) && (
                <DrawerSection
                  title="Election statistics"
                  right={canUpdate ? (
                    <button className="erp-link text-erp-xs" onClick={() => setStatsOpen(true)}>
                      {data.statistics.length > 0 ? 'Edit' : 'Record'}
                    </button>
                  ) : undefined}
                >
                  {data.statistics.length === 0 ? (
                    <p className="px-4 py-3 text-erp-xs text-muted-foreground">
                      No figures recorded. Turnout stays blank until a year is recorded here
                      or loaded through the statistics import.
                    </p>
                  ) : (
                  <div className="px-0">
                    <ErpTable
                      head={<><Th>Year</Th><Th>Registered</Th><Th>Cast</Th><Th>Turnout</Th><Th className="axis-hide-sm">Source</Th></>}
                    >
                      {data.statistics.map((s) => (
                        <tr key={s.electionYear}>
                          <Td className="erp-metric">{s.electionYear}</Td>
                          <Td className="erp-metric">{fmtNumber(s.registeredVoters ?? null)}</Td>
                          <Td className="erp-metric">{fmtNumber(s.votesCast ?? null)}</Td>
                          <Td className="erp-metric">
                            {s.turnoutPct === null || s.turnoutPct === undefined ? '—' : `${s.turnoutPct}%`}
                          </Td>
                          <Td className="axis-hide-sm">{s.source ?? '—'}</Td>
                        </tr>
                      ))}
                    </ErpTable>
                  </div>
                  )}
                </DrawerSection>
              )}

              {u.attributes && Object.keys(u.attributes).length > 0 && (
                <DrawerSection title="Attributes">
                  <div className="px-4 py-3">
                    <pre className="text-erp-xs font-mono bg-muted/40 border border-border rounded-md p-3 overflow-x-auto whitespace-pre-wrap break-words">
                      {JSON.stringify(u.attributes, null, 2)}
                    </pre>
                  </div>
                </DrawerSection>
              )}

              <DrawerSection title="Record">
                <DrawerField label="Unit ID" value={u.id} mono />
                <DrawerField label="Created" value={fmtDateTime(u.createdAt)} />
                <DrawerField label="Updated" value={fmtDateTime(u.updatedAt)} />
                <DrawerField label="Version" value={<span className="erp-metric">{u.version}</span>} />
              </DrawerSection>
            </>
          )}
        </>
      )}
      <StatisticsDialog
        detail={statsOpen ? data : null}
        onClose={() => setStatsOpen(false)}
        onSaved={reload}
      />
      <AssessmentDialog
        detail={assessOpen ? data : null}
        onClose={() => setAssessOpen(false)}
        onSaved={reload}
      />
    </Drawer>
  )
}
